import { useState } from 'react'
import type { RegisterFormData } from './useRegisterValidation'

interface UseRegisterFormStateReturn {
    formData: RegisterFormData
    error: string | null
    inviteKeyError: string | null
    loading: boolean
    showPassword: boolean
    showConfirmPassword: boolean
    touchedFields: {
        username: boolean
        email: boolean
        password: boolean
        confirmPassword: boolean
    }
    setFormData: React.Dispatch<React.SetStateAction<RegisterFormData>>
    setError: React.Dispatch<React.SetStateAction<string | null>>
    setInviteKeyError: React.Dispatch<React.SetStateAction<string | null>>
    setLoading: React.Dispatch<React.SetStateAction<boolean>>
    handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    handleSelectChange: (e: { target: { name: string; value: string } }) => void
    handleTogglePasswordVisibility: () => void
    handleToggleConfirmPasswordVisibility: () => void
}

export const useRegisterFormState = (): UseRegisterFormStateReturn => {
    const [formData, setFormData] = useState<RegisterFormData>({
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        userLang: 'en',
        targetLang: 'fr',
        inviteKey: '',
    })
    const [error, setError] = useState<string | null>(null)
    const [inviteKeyError, setInviteKeyError] = useState<string | null>(null)
    const [loading, setLoading] = useState(false)
    const [showPassword, setShowPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)
    const [touchedFields, setTouchedFields] = useState({
        username: false,
        email: false,
        password: false,
        confirmPassword: false,
    })

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const fieldName = e.target.name as keyof typeof touchedFields
        setFormData({
            ...formData,
            [fieldName]: e.target.value,
        })

        // Mark field as touched
        if (fieldName in touchedFields) {
            setTouchedFields((prev) => ({
                ...prev,
                [fieldName]: true,
            }))
        }

        // Clear field-level invite-key error as the user types a new value
        if (e.target.name === 'inviteKey') {
            setInviteKeyError(null)
        }
    }

    const handleSelectChange = (e: { target: { name: string; value: string } }) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        })
    }

    const handleTogglePasswordVisibility = () => {
        setShowPassword(!showPassword)
    }

    const handleToggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(!showConfirmPassword)
    }

    return {
        formData,
        error,
        inviteKeyError,
        loading,
        showPassword,
        showConfirmPassword,
        touchedFields,
        setFormData,
        setError,
        setInviteKeyError,
        setLoading,
        handleChange,
        handleSelectChange,
        handleTogglePasswordVisibility,
        handleToggleConfirmPasswordVisibility,
    }
}
